import type React from 'react';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import type {GalleryPageURI} from '@Components/design-gallery/design-gallery.types';
import type {DesignSelection} from '@Components/social-media/social-media.types';
import type {PosterPageData} from '@Libraries/poster-library';
import {MOBILE_SCREEN_BREAKPOINT} from '@Utils/dom.util';

export interface OnPosterCreatedCallbackParams {
  newlyCreatedPosterHashedId: string;
  currentIndex?: number;
  totalPosterCreationsCount?: number;
  /** The design was composed on one timeline (a video selection), so the editor should open with the timeline shown. */
  shouldOpenTimeline?: boolean;
}

export interface CreateDesignFromMediaModalOpts {
  allowMultipagePosterCreation: boolean;
  /** A multi-selection containing a video becomes a single sequential video page instead of a multipage design. */
  createSequentialDesignForVideoSelections?: boolean;
  maxSelectionNumber: number;
  shouldUseFastPendingImageUploadFlow?: boolean;
  onTriggered?: () => void;
  onPosterCreated: (params: OnPosterCreatedCallbackParams) => void;
  onAllPostersCreated?: (newlyCreatedPosterHashedIds: string[], failedUploads: File[]) => void;
  onPosterCreationsStarted?: () => void;
  onErrorDuringUpload?: (file: File, currentUploadIndex?: number, totalItemsToUpload?: number) => void;
  idFolder?: string;
  footerPrimaryCtaText?: string;
  hideFooterSecondaryActions?: boolean;
}

export enum DesignSelectorTab {
  MY_DESIGNS = 'design-selector-my-designs',
  SHARED_WITH_ME = 'design-selector-shared-with-me',
  GALLERY_TEMPLATES = 'design-selector-gallery-templates',
}

export enum LimitExceededAction {
  /** Roll back the click; selection stays at the current size. The default behavior when no callback is supplied. */
  REJECT = 'reject',
  /** Accept the click even though it exceeds the limit. Use this if the caller wants to show their own warning UI but keep the user's selection. */
  ALLOW = 'allow',
  /** Auto-deselect the oldest selections to make room for the new click. */
  EVICT_OLDEST = 'evict-oldest',
}

/**
 * Info passed to `onLimitExceeded` when a click would push the selection past `maxSelection`.
 *
 * Generic over the selection item type so the same shape serves both the React `<DesignSelector>`
 * prop (defaults to `DesignSelection` — the `{posterHashedId, pageHashedId}` form React works
 * with) and the vanilla `window.PMW.designSelector` opts (uses `PosterPageData` — the
 * `{posterId, pageId, previewUrl, ...}` form the coordinator yields).
 */
export interface LimitExceededInfo<TItem = DesignSelection> {
  /** The selection state before this click was processed. */
  currentSelection: TItem[];
  /** What the selection would become if the click were accepted as-is. */
  tentativeSelection: TItem[];
  /** The items the user just clicked to add (the click delta). */
  newlyAdded: TItem[];
  /** The configured max. */
  maxSelection: number;
  /**
   * True when the click would add more than one item at once — i.e. the multipage popup's "Select All" button,
   * not an individual page click. Useful for callers who want EVICT_OLDEST on single clicks but REJECT on bulk
   * (since EVICT_OLDEST on a bulk add produces a confusing "X of Y selected" partial result).
   */
  isBulkAdd: boolean;
}

export interface DesignSelectorProps {
  onDesignsSelection?: (designsSelectionInformation: DesignSelection[]) => void;
  initialDesignSelections?: DesignSelection[];
  containerRef: React.RefObject<HTMLDivElement | null>;
  onGalleryTemplateSelectionCallback?: (designs: DesignSelection[]) => void;
  onTabSelected?: (newTabSelection: DesignSelectorTab) => void;
  designSelectorTab?: DesignSelectorTab;
  startDesignFromMediaModalBtnOpts?: CreateDesignFromMediaModalOpts;
  isLoading?: boolean;
  designGalleryStartingURI?: GalleryPageURI;
  hideTabContainer?: boolean;
  desktopClasses?: string;
  mobileClasses?: string;
  designGalleryHeight?: string;
  maxSelection?: number;
  /**
   * Called when the user attempts to exceed `maxSelection`. The returned action decides what happens:
   */
  onLimitExceeded?: (info: LimitExceededInfo) => LimitExceededAction;
  scrollContainerEl?: HTMLElement | Window | null;
}

export interface DesignSelectorPageLimitBanner {
  threshold: number;
  desktopMessageKey: string;
  mobileMessageKey: string;
}

/**
 * Opts for `window.PMW.designSelector.open` (modal mode).
 */
export interface DesignSelectorOpts {
  title?: string;
  addSelectedCtaText?: string;
  preselectedDesigns?: (PosterPageData | DesignSelection)[];
  isMultiSelectionAllowed?: boolean;
  doSelectPosterOnly?: boolean;
  displayOnlyUnsharedFromTeamSpace?: boolean;
  maxSelection?: number;
  onLimitExceeded?: (info: LimitExceededInfo<PosterPageData>) => LimitExceededAction;
  addCheckmarkOnSelection?: boolean;
  pageLimitBanner?: DesignSelectorPageLimitBanner;
  selectedNameClass?: string;
  onDialogLoaded?: () => void;
  onSelectionChange?: (selection: PosterPageData[]) => void;
  onAddSelected?: (selection: PosterPageData[]) => void;
  startFromMediaModalBtnOpts?: CreateDesignFromMediaModalOpts;
  unhideSelectAllButton?: boolean;
}

/**
 * Opts for `window.PMW.designSelector.openEmbedded` (embedded mode).
 */
export interface DesignSelectorEmbeddedOpts extends Omit<DesignSelectorOpts, 'title'> {
  /** CSS selector or DOM element for the element to mount the design selector into. */
  divToLoadIn: string | Element;
  scrollContainerEl?: HTMLElement | Window | null;
  loadPostersIfNotLoadedEventType?: string;
  onLoaded?: () => void;
}

export const containerQuery: ContainerQueryObject = {
  small: {
    minWidth: 0,
    maxWidth: MOBILE_SCREEN_BREAKPOINT,
  },
  large: {
    minWidth: MOBILE_SCREEN_BREAKPOINT + 1,
  },
};

export const skeletonLoaderContainerQuery: ContainerQueryObject = {
  xxsmall: {
    minWidth: 0,
    maxWidth: 499,
  },
  xsmall: {
    minWidth: 500,
    maxWidth: 599,
  },
  small: {
    minWidth: 600,
    maxWidth: 768,
  },
  medium: {
    minWidth: 769,
    maxWidth: 819,
  },
  large: {
    minWidth: 820,
    maxWidth: 899,
  },
  xlarge: {
    minWidth: 900,
    maxWidth: 999,
  },
  xxlarge: {
    minWidth: 1000,
    maxWidth: 1099,
  },
  xxxlarge: {
    minWidth: 1100,
  },
};
